// AdSense for the ad tier (FREE_PLAY): profiles without the $5 unlock see one lobby display unit and, through the
// H5 Games Ads Ad Placement API, a preroll and 'next' breaks on Play again. Google decides whether an ad shows at all
// (frequency hint 120 s, its own floor of 30 s); until H5 is approved for the account, breaks simply never fill.
// The tag is only ever added for unpaid profiles; paying turns everything off for the rest of the visit.
export interface AdsConfig { client: string; slot: string | null }
type Push = (o: Record<string, unknown>) => void;
interface AdWindow { adsbygoogle?: unknown[] }

const TAG = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
const BREAK_GRACE_MS = 1500; // no answer from the tag this long (blocked, not loaded): carry on without an ad
const CLIENT_RE = /^ca-pub-\d{10,20}$/;

export class Ads {
  private cfg: AdsConfig | null = null;
  private off = false; // paid this visit, or never configured
  private prerolled = false;
  private bannerDone = false;

  constructor(private readonly hooks: { test?: boolean; mute?(on: boolean): void } = {}) {}

  get active(): boolean { return !!this.cfg && !this.off; }

  // The profile's ads field from the Worker: null = no ads for this profile (paid, or AdSense not configured).
  configure(cfg: AdsConfig | null): void {
    if (!cfg || !CLIENT_RE.test(cfg.client)) { this.disable(); return; }
    if (this.cfg || this.off) return; // once per visit; a paid profile stays off
    this.cfg = cfg;
    const w = window as unknown as AdWindow;
    w.adsbygoogle = w.adsbygoogle || [];
    const s = document.createElement('script');
    s.async = true;
    s.src = `${TAG}?client=${encodeURIComponent(cfg.client)}`;
    s.crossOrigin = 'anonymous';
    s.dataset.adClient = cfg.client;
    s.dataset.adFrequencyHint = '120s';
    if (this.hooks.test) s.dataset.adbreakTest = 'on';
    document.head.appendChild(s);
    this.push({ preloadAdBreaks: 'on', sound: 'on' }); // adConfig
  }

  // Paid (or no ads for this profile): no more breaks, and the banner goes.
  disable(): void {
    this.off = true;
    document.getElementById('adLobby')?.classList.add('hidden');
  }

  // Once per page load, before the first game (Google may skip it).
  preroll(done: () => void): void {
    if (!this.active || this.prerolled) { done(); return; }
    this.prerolled = true;
    this.adBreak({ type: 'preroll' }, done);
  }

  // Between lives ("Play again"): the game is on the death screen, so nothing needs pausing but the sound.
  next(done: () => void): void {
    if (!this.active) { done(); return; }
    this.adBreak({ type: 'next', name: 'play-again' }, done);
  }

  // The lobby display unit: requested once per page load, never refreshed (AdSense policy), hidden while playing
  // with the lobby itself.
  banner(): void {
    const box = document.getElementById('adLobby');
    if (!box || !this.active || !this.cfg?.slot) return;
    box.classList.remove('hidden');
    if (this.bannerDone) return;
    this.bannerDone = true;
    const ins = document.createElement('ins');
    ins.className = 'adsbygoogle';
    ins.dataset.adClient = this.cfg.client;
    ins.dataset.adSlot = this.cfg.slot;
    ins.dataset.adFormat = 'auto';
    ins.dataset.fullWidthResponsive = 'true';
    if (this.hooks.test) ins.dataset.adtest = 'on';
    box.replaceChildren(ins);
    this.push({});
  }

  private push: Push = o => { ((window as unknown as AdWindow).adsbygoogle ??= []).push(o); };

  // adBreakDone always fires once the tag runs; if it never loads (blocked), the grace timer carries on instead.
  private adBreak(o: Record<string, unknown>, done: () => void): void {
    let finished = false, showing = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      if (showing) this.hooks.mute?.(false);
      done();
    };
    const timer = setTimeout(() => { if (!showing) finish(); }, BREAK_GRACE_MS);
    this.push({
      ...o,
      beforeAd: () => { showing = true; this.hooks.mute?.(true); },
      afterAd: () => { this.hooks.mute?.(false); showing = false; },
      adBreakDone: finish,
    });
  }
}
